import { useEffect, useMemo, useState } from "react";
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Button,
  Dropdown,
  Option,
  Field,
  Input,
  Radio,
  RadioGroup,
  Checkbox,
  Body1,
  Body1Strong,
  Divider,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import type { AdminUser, Department } from "../pages/AdminUsers";

interface Props {
  open: boolean;
  user: AdminUser | null;
  allUsers: AdminUser[];
  departments: Department[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: (displayName: string) => void;
}

interface BlockingRefs {
  managerOf: number;
  leaveRequestsAsRequester: number;
  leaveRequestsAsApprover: number;
  leaveBalances: number;
  delegations: number;
  fmlaAttachments: number;
}

const useStyles = makeStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM
  },
  rolesRow: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS
  },
  hint: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXL,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`
  },
  dangerZone: {
    marginTop: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS
  },
  refsList: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
    color: tokens.colorPaletteRedForeground1
  }
});

function formatBlockingRefs(b: BlockingRefs): string[] {
  const out: string[] = [];
  if (b.managerOf > 0) out.push(`is the manager of ${b.managerOf} user${b.managerOf === 1 ? "" : "s"}`);
  if (b.leaveRequestsAsRequester > 0)
    out.push(`has ${b.leaveRequestsAsRequester} historical leave request${b.leaveRequestsAsRequester === 1 ? "" : "s"}`);
  if (b.leaveRequestsAsApprover > 0)
    out.push(`approved ${b.leaveRequestsAsApprover} leave request${b.leaveRequestsAsApprover === 1 ? "" : "s"}`);
  if (b.leaveBalances > 0)
    out.push(`has ${b.leaveBalances} balance row${b.leaveBalances === 1 ? "" : "s"}`);
  if (b.delegations > 0)
    out.push(`appears in ${b.delegations} substitute delegation${b.delegations === 1 ? "" : "s"}`);
  if (b.fmlaAttachments > 0)
    out.push(`uploaded ${b.fmlaAttachments} FMLA attachment${b.fmlaAttachments === 1 ? "" : "s"}`);
  return out;
}

const NO_MANAGER = "__none__";
const NO_DEPARTMENT = "__none__";

export default function EditUserDrawer({ open, user, allUsers, departments, onClose, onSaved, onDeleted }: Props) {
  const styles = useStyles();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [departmentId, setDepartmentId] = useState<string>(NO_DEPARTMENT);
  const [managerId, setManagerId] = useState<string>(NO_MANAGER);
  const [status, setStatus] = useState<"active" | "ex_employee">("active");
  const [hasManagerRole, setHasManagerRole] = useState(false);
  const [hasAdminRole, setHasAdminRole] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [blockingRefs, setBlockingRefs] = useState<BlockingRefs | null>(null);

  useEffect(() => {
    if (open && user) {
      setEmail(user.email);
      setDisplayName(user.displayName);
      setJobTitle(user.jobTitle ?? "");
      setDepartmentId(user.departmentId ?? NO_DEPARTMENT);
      setManagerId(user.managerId ?? NO_MANAGER);
      setStatus(user.isExEmployee ? "ex_employee" : "active");
      setHasManagerRole(user.roles.includes("manager"));
      setHasAdminRole(user.roles.includes("administrator"));
      setError(null);
      setSubmitting(false);
      setConfirmDelete(false);
      setDeleting(false);
      setBlockingRefs(null);
    }
  }, [open, user]);

  const managerOptions = useMemo(() => {
    // Only active, non-ex-employee users; exclude the target so self-manager
    // is impossible from the UI.
    if (!user) return [];
    return allUsers
      .filter((u) => u.id !== user.id && u.isActive && !u.isExEmployee)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [user, allUsers]);

  const selectedDeptName = useMemo(() => {
    if (departmentId === NO_DEPARTMENT) return "(none)";
    return departments.find((d) => d.id === departmentId)?.name ?? "";
  }, [departmentId, departments]);

  const selectedManagerName = useMemo(() => {
    if (managerId === NO_MANAGER) return "(none)";
    return managerOptions.find((u) => u.id === managerId)?.displayName ?? "";
  }, [managerId, managerOptions]);

  if (!user) return null;

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const emailIsPlaceholder = email.trim().toLowerCase().endsWith(".placeholder");
  const nameValid = displayName.trim().length > 0;
  const canSubmit = emailLooksValid && !emailIsPlaceholder && nameValid && !submitting;

  async function save() {
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      // 1) PATCH the user fields that changed.
      const patch: Record<string, unknown> = {};
      const e = email.trim().toLowerCase();
      if (e !== user.email.toLowerCase()) patch.email = e;
      if (displayName.trim() !== user.displayName) patch.displayName = displayName.trim();
      const jt = jobTitle.trim() || null;
      if (jt !== (user.jobTitle ?? null)) patch.jobTitle = jt;
      const dept = departmentId === NO_DEPARTMENT ? null : departmentId;
      if (dept !== (user.departmentId ?? null)) patch.departmentId = dept;
      const mgr = managerId === NO_MANAGER ? null : managerId;
      if (mgr !== (user.managerId ?? null)) patch.managerId = mgr;

      // Single status maps to both is_active + is_ex_employee. Always send both
      // so we normalize any orphan rows (e.g. legacy is_active=0 + is_ex_employee=0).
      const wantActive = status === "active";
      const wantEx = status === "ex_employee";
      if (wantActive !== user.isActive) patch.isActive = wantActive;
      if (wantEx !== user.isExEmployee) patch.isExEmployee = wantEx;

      if (Object.keys(patch).length > 0) {
        const r = await fetch(`/api/manage-users/${encodeURIComponent(user.id)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch)
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
      }

      // 2) Role diffs: grant/revoke as needed.
      const wantManager = hasManagerRole;
      const hadManager = user.roles.includes("manager");
      if (wantManager && !hadManager) {
        await callRole(user.id, "POST", "manager");
      } else if (!wantManager && hadManager) {
        await callRole(user.id, "DELETE", "manager");
      }

      const wantAdmin = hasAdminRole;
      const hadAdmin = user.roles.includes("administrator");
      if (wantAdmin && !hadAdmin) {
        await callRole(user.id, "POST", "administrator");
      } else if (!wantAdmin && hadAdmin) {
        await callRole(user.id, "DELETE", "administrator");
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteUser() {
    if (!user) return;
    setDeleting(true);
    setError(null);
    setBlockingRefs(null);
    try {
      const r = await fetch(`/api/manage-users/${encodeURIComponent(user.id)}`, { method: "DELETE" });
      if (r.status === 200) {
        const name = user.displayName;
        setConfirmDelete(false);
        onDeleted(name);
        onClose();
        return;
      }
      const body = await r.json().catch(() => ({}));
      if (r.status === 409 && body.references) {
        setBlockingRefs(body.references as BlockingRefs);
        setError(body.error ?? "User has historical references and cannot be hard-deleted");
      } else {
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  }

  async function callRole(userId: string, method: "POST" | "DELETE", role: string) {
    const url =
      method === "POST"
        ? `/api/manage-users/${encodeURIComponent(userId)}/roles`
        : `/api/manage-users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(role)}`;
    const init: RequestInit =
      method === "POST"
        ? { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ role }) }
        : { method };
    const r = await fetch(url, init);
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${r.status}`);
    }
  }

  return (
    <OverlayDrawer open={open} onOpenChange={(_, d) => !d.open && onClose()} position="end" size="medium">
      <DrawerHeader>
        <DrawerHeaderTitle>Edit user</DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.body}>
          <Field
            label="Email"
            required
            validationState={!email || (emailLooksValid && !emailIsPlaceholder) ? undefined : "error"}
            validationMessage={
              !email
                ? undefined
                : !emailLooksValid
                ? "Doesn't look like an email address"
                : emailIsPlaceholder
                ? "Placeholder emails cannot be saved — fix this to give the user a real address"
                : undefined
            }
          >
            <Input value={email} onChange={(_, d) => setEmail(d.value)} />
          </Field>

          <Field
            label="Display name"
            required
            validationState={displayName && !nameValid ? "error" : undefined}
            validationMessage={displayName && !nameValid ? "Cannot be empty" : undefined}
          >
            <Input value={displayName} onChange={(_, d) => setDisplayName(d.value)} />
          </Field>

          <Field label="Job title">
            <Input value={jobTitle} onChange={(_, d) => setJobTitle(d.value)} />
          </Field>

          <Field label="Department">
            <Dropdown
              selectedOptions={[departmentId]}
              value={selectedDeptName}
              onOptionSelect={(_, d) => setDepartmentId(String(d.optionValue ?? NO_DEPARTMENT))}
            >
              <Option value={NO_DEPARTMENT} text="(none)">(none)</Option>
              {departments.map((d) => (
                <Option key={d.id} value={d.id} text={d.name}>{d.name}</Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Manager">
            <Dropdown
              selectedOptions={[managerId]}
              value={selectedManagerName}
              onOptionSelect={(_, d) => setManagerId(String(d.optionValue ?? NO_MANAGER))}
            >
              <Option value={NO_MANAGER} text="(none)">(none)</Option>
              {managerOptions.map((m) => (
                <Option key={m.id} value={m.id} text={m.displayName}>
                  {m.displayName}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Status">
            <RadioGroup
              layout="horizontal"
              value={status}
              onChange={(_, d) => setStatus(d.value === "ex_employee" ? "ex_employee" : "active")}
            >
              <Radio value="active" label="Active" />
              <Radio value="ex_employee" label="Ex-employee" />
            </RadioGroup>
          </Field>

          <Divider />

          <Body1>Roles</Body1>
          <div className={styles.rolesRow}>
            <Checkbox
              label="Employee (implicit for active users)"
              checked
              disabled
            />
            <Checkbox
              label="Manager — can approve direct reports' requests"
              checked={hasManagerRole}
              onChange={(_, d) => setHasManagerRole(!!d.checked)}
            />
            <Checkbox
              label="Administrator — full access to all users, requests, and balances"
              checked={hasAdminRole}
              onChange={(_, d) => setHasAdminRole(!!d.checked)}
            />
          </div>
          <Body1 className={styles.hint}>
            Role changes take effect on the user's next sign-in. They may need to sign out and back in to see new nav links.
          </Body1>

          {error && !blockingRefs && <Body1 className={styles.error}>{error}</Body1>}

          <div className={styles.dangerZone}>
            <Body1Strong>Danger zone</Body1Strong>
            <Body1>
              Hard-deletes this user. Only works if they have no historical leave requests,
              balances, or other references. Recoverable via the 35-day database restore window;
              the audit log also retains a full snapshot.
            </Body1>
            {blockingRefs && (
              <div>
                <Body1 className={styles.error}>Cannot delete — {displayName} {" "}</Body1>
                <ul className={styles.refsList}>
                  {formatBlockingRefs(blockingRefs).map((line) => (
                    <li key={line}><Body1 className={styles.error}>{line}</Body1></li>
                  ))}
                </ul>
                <Body1 className={styles.hint}>
                  Tip: mark them as ex-employee + inactive instead to hide them from default views.
                </Body1>
              </div>
            )}
            <div>
              <Button
                appearance="secondary"
                disabled={deleting || submitting}
                onClick={() => { setBlockingRefs(null); setError(null); setConfirmDelete(true); }}
              >
                Delete user permanently
              </Button>
            </div>
          </div>

          <div className={styles.footer}>
            <Button appearance="secondary" onClick={onClose} disabled={submitting || deleting}>Cancel</Button>
            <Button appearance="primary" onClick={save} disabled={!canSubmit || deleting}>
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </DrawerBody>

      <Dialog open={confirmDelete} onOpenChange={(_, d) => !d.open && setConfirmDelete(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete {displayName}?</DialogTitle>
            <DialogContent>
              <Body1 as="p">
                This permanently removes the user and any role assignments. The audit log keeps a
                snapshot, and the database has a 35-day point-in-time restore window. This cannot
                be undone from the app.
              </Body1>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={deleteUser} disabled={deleting}>
                {deleting ? "Deleting…" : "Yes, delete"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </OverlayDrawer>
  );
}
